import React, { useState, useEffect } from 'react';
import {
  Tag,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  AlertTriangle,
  Users,
  Search
} from 'lucide-react';
import { tournamentService, TournamentCategory } from '../services/TournamentService';
import { supabase } from '../lib/supabase';

interface UserWithCategories {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  categories: string[];
}

export const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<TournamentCategory[]>([]);
  const [users, setUsers] = useState<UserWithCategories[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUserCategoriesModal, setShowUserCategoriesModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<TournamentCategory | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithCategories | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'categories' | 'users'>('categories');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, usersData] = await Promise.all([
        tournamentService.getAllCategories(),
        loadUsersWithCategories()
      ]);
      setCategories(categoriesData);
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const loadUsersWithCategories = async (): Promise<UserWithCategories[]> => {
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, full_name, avatar_url')
      .order('username');

    if (profilesError) throw profilesError;

    const { data: userCategories, error: categoriesError } = await supabase
      .from('user_categories')
      .select('user_id, category_id');

    if (categoriesError) throw categoriesError;

    return (profiles || []).map(profile => ({
      ...profile,
      categories: (userCategories || [])
        .filter(uc => uc.user_id === profile.id)
        .map(uc => uc.category_id)
    }));
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      await tournamentService.deleteCategory(selectedCategory.id);
      setSuccess('Category deleted successfully');
      setShowDeleteModal(false);
      setSelectedCategory(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category');
    }
  };

  const handleToggleUserCategory = async (userId: string, categoryId: string, isAssigned: boolean) => {
    try {
      if (isAssigned) {
        await tournamentService.removeCategoryFromUser(userId, categoryId);
        setSuccess('Category removed from user');
      } else {
        await tournamentService.assignCategoryToUser(userId, categoryId);
        setSuccess('Category assigned to user');
      }
      await loadData();
    } catch (err) {
      console.error('Error toggling user category:', err);
      setError('Failed to update user category');
    }
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cat.description && cat.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.full_name && user.full_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Category Management</h1>
        {activeTab === 'categories' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus size={18} className="mr-2" />
            Create Category
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-center justify-between text-red-500">
          <div className="flex items-center">
            <AlertTriangle size={20} className="mr-2" />
            <p>{error}</p>
          </div>
          <button onClick={() => setError(null)}>
            <X size={18} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 flex items-center justify-between text-green-500">
          <div className="flex items-center">
            <Check size={20} className="mr-2" />
            <p>{success}</p>
          </div>
          <button onClick={() => setSuccess(null)}>
            <X size={18} />
          </button>
        </div>
      )}

      <div className="flex border-b border-kod-purple-600 mb-6">
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-3 font-medium relative ${
            activeTab === 'categories'
              ? 'text-kod-accent'
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <div className="flex items-center">
            <Tag size={18} className="mr-2" />
            Categories
          </div>
          {activeTab === 'categories' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-kod-accent"></div>
          )}
        </button>

        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-3 font-medium relative ${
            activeTab === 'users'
              ? 'text-kod-accent'
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <div className="flex items-center">
            <Users size={18} className="mr-2" />
            User Categories
          </div>
          {activeTab === 'users' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-kod-accent"></div>
          )}
        </button>
      </div>

      <div className="card mb-6">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
          <input
            type="text"
            placeholder={activeTab === 'categories' ? 'Search categories...' : 'Search users...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-kod-accent"
          />
        </div>
      </div>

      {activeTab === 'categories' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCategories.map((category) => {
            const userCount = users.filter(u => u.categories.includes(category.id)).length;
            return (
              <div key={category.id} className="card hover:bg-kod-purple-700/50 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center flex-1">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center mr-3"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <Tag size={24} style={{ color: category.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{category.name}</h3>
                      <p className="text-sm text-white/60">{userCount} users</p>
                    </div>
                  </div>
                </div>

                {category.description && (
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">{category.description}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowEditModal(true);
                    }}
                    className="flex-1 btn-outline flex items-center justify-center"
                  >
                    <Edit2 size={16} className="mr-2" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowDeleteModal(true);
                    }}
                    className="btn-outline text-red-400 hover:bg-red-500/10 hover:border-red-500/50 flex items-center justify-center px-4"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}

          {filteredCategories.length === 0 && (
            <div className="col-span-full card text-center py-12">
              <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
                <Tag size={36} className="text-white/50" />
              </div>
              <h2 className="text-xl font-bold mb-2">No Categories Found</h2>
              <p className="text-white/70 mb-6">
                {searchQuery ? 'Try adjusting your search query' : 'Create your first category to get started'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredUsers.map((user) => (
            <div key={user.id} className="card hover:bg-kod-purple-700/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <div className="w-12 h-12 rounded-full bg-kod-purple-700 overflow-hidden mr-4">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.username} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl font-bold">
                        {user.username[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold">{user.username}</h3>
                    {user.full_name && <p className="text-sm text-white/60">{user.full_name}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex flex-wrap gap-2 mr-4">
                    {user.categories.length > 0 ? (
                      user.categories.map(categoryId => {
                        const category = categories.find(c => c.id === categoryId);
                        return category ? (
                          <span
                            key={categoryId}
                            className="px-3 py-1 rounded-full text-xs font-bold"
                            style={{
                              backgroundColor: `${category.color}20`,
                              color: category.color
                            }}
                          >
                            {category.name}
                          </span>
                        ) : null;
                      })
                    ) : (
                      <span className="text-sm text-white/50">No categories</span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setShowUserCategoriesModal(true);
                    }}
                    className="btn-outline"
                  >
                    Manage
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredUsers.length === 0 && (
            <div className="card text-center py-12">
              <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
                <Users size={36} className="text-white/50" />
              </div>
              <h2 className="text-xl font-bold mb-2">No Users Found</h2>
              <p className="text-white/70">Try adjusting your search query</p>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <CategoryFormModal
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            try {
              await tournamentService.createCategory(data.name, data.description, data.color);
              setSuccess('Category created successfully');
              setShowCreateModal(false);
              await loadData();
            } catch (err) {
              console.error('Error creating category:', err);
              setError('Failed to create category');
            }
          }}
        />
      )}

      {showEditModal && selectedCategory && (
        <CategoryFormModal
          category={selectedCategory}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCategory(null);
          }}
          onSave={async (data) => {
            try {
              await tournamentService.updateCategory(selectedCategory.id, data);
              setSuccess('Category updated successfully');
              setShowEditModal(false);
              setSelectedCategory(null);
              await loadData();
            } catch (err) {
              console.error('Error updating category:', err);
              setError('Failed to update category');
            }
          }}
        />
      )}

      {showDeleteModal && selectedCategory && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-red-500/20 p-3 rounded-full mr-4">
                <AlertTriangle size={24} className="text-red-400" />
              </div>
              <h3 className="text-xl font-bold">Delete Category</h3>
            </div>
            <p className="text-white/70 mb-6">
              Are you sure you want to delete "{selectedCategory.name}"? This will remove the category from all users and tournaments.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedCategory(null);
                }}
                className="flex-1 btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCategory}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showUserCategoriesModal && selectedUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-kod-purple-600">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Manage Categories - {selectedUser.username}</h3>
                <button
                  onClick={() => {
                    setShowUserCategoriesModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-white/70 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-2">
              {categories.map(category => {
                const isAssigned = selectedUser.categories.includes(category.id);
                return (
                  <label
                    key={category.id}
                    className="flex items-center justify-between p-3 hover:bg-kod-purple-700/50 rounded-lg cursor-pointer"
                  >
                    <div className="flex items-center flex-1">
                      <div
                        className="w-8 h-8 rounded flex items-center justify-center mr-3"
                        style={{ backgroundColor: `${category.color}20` }}
                      >
                        <Tag size={16} style={{ color: category.color }} />
                      </div>
                      <div>
                        <div className="font-medium">{category.name}</div>
                        {category.description && (
                          <div className="text-sm text-white/60">{category.description}</div>
                        )}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      onChange={() => handleToggleUserCategory(selectedUser.id, category.id, isAssigned)}
                      className="ml-3"
                    />
                  </label>
                );
              })}
            </div>

            <div className="p-6 border-t border-kod-purple-600">
              <button
                onClick={() => {
                  setShowUserCategoriesModal(false);
                  setSelectedUser(null);
                }}
                className="btn-outline w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface CategoryFormModalProps {
  category?: TournamentCategory;
  onClose: () => void;
  onSave: (data: { name: string; description?: string; color: string }) => Promise<void>;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    color: category?.color || '#6366f1'
  });
  const [saving, setSaving] = useState(false);

  const predefinedColors = [
    '#6366f1', // Indigo
    '#10b981', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#14b8a6', // Teal
    '#f97316', // Orange
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-kod-purple-800 rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-kod-purple-600">
          <h3 className="text-xl font-bold">
            {category ? 'Edit Category' : 'Create Category'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Color</label>
            <div className="grid grid-cols-8 gap-2 mb-3">
              {predefinedColors.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full transition-all ${
                    formData.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-kod-purple-800' : ''
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              className="w-full h-10 bg-kod-purple-700 border border-kod-purple-600 rounded-lg"
            />
          </div>
        </form>

        <div className="p-6 border-t border-kod-purple-600 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 btn-outline"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 btn-primary"
          >
            {saving ? 'Saving...' : category ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};
